import { useState } from 'react'
import { Tag, Table, Tooltip } from '@/components/ui'
import { EditOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import ColumnMappingModal from '../ColumnMappingModal'
import type { ColumnMapping, RegistrationField } from '@/pages/events/attendees/types'

interface MatchColumnStepProps {
  headers: string[]
  previewData: Record<string, string>[]
  columnMapping: ColumnMapping
  registrationFields: RegistrationField[]
  onColumnMappingChange: (mapping: ColumnMapping) => void
  mappedCount: number
  skippedCount: number
}

export default function MatchColumnStep({
  headers,
  previewData,
  columnMapping,
  registrationFields,
  onColumnMappingChange,
  mappedCount,
  skippedCount,
}: MatchColumnStepProps) {
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null)
  const [mappingModalOpen, setMappingModalOpen] = useState(false)

  const handleColumnClick = (header: string) => {
    setSelectedColumn(header)
    setMappingModalOpen(true)
  }

  const handleMappingChange = (fieldIdentifier: string | null) => {
    if (selectedColumn) {
      onColumnMappingChange({
        ...columnMapping,
        [selectedColumn]: fieldIdentifier,
      })
    }
    setMappingModalOpen(false)
    setSelectedColumn(null)
  }

  const getColumnStatus = (header: string): 'mapped' | 'unmapped' | 'skipped' => {
    const mapping = columnMapping[header]
    if (mapping === null) return 'skipped'
    if (mapping) return 'mapped'
    return 'unmapped'
  }

  const getTagColor = (status: 'mapped' | 'unmapped' | 'skipped'): string => {
    switch (status) {
      case 'mapped':
        return 'success'
      case 'unmapped':
        return 'warning'
      case 'skipped':
        return 'default'
    }
  }

  const getMappedFieldLabel = (header: string): string => {
    const fieldIdentifier = columnMapping[header]
    if (!fieldIdentifier) return header
    const field = registrationFields.find(f => f.identifier === fieldIdentifier)
    return field?.label || fieldIdentifier
  }

  // Build table columns
  const columns = headers.map((header) => {
    const status = getColumnStatus(header)
    const mappedLabel = getMappedFieldLabel(header)

    return {
      title: (
        <Tooltip title={`Click to ${status === 'mapped' ? 'change' : 'map'} this column`}>
          <div
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
            onClick={() => handleColumnClick(header)}
          >
            <Tag color={getTagColor(status)}>
              {status === 'mapped' ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <CheckCircleOutlined />
                  {mappedLabel}
                </span>
              ) : status === 'skipped' ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <CloseCircleOutlined />
                  Skipped
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {header}
                  <EditOutlined />
                </span>
              )}
            </Tag>
          </div>
        </Tooltip>
      ),
      dataIndex: header,
      key: header,
      width: 150,
      ellipsis: true,
    }
  })

  // Build table data from preview
  const dataSource = previewData.slice(0, 5).map((row, index) => ({
    key: index,
    ...row,
  }))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
      <div>
        <h3 style={{ fontSize: '16px', fontWeight: 500, color: '#111827', marginBottom: '8px' }}>
          Map CSV columns to registration fields
        </h3>
        <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
          Click on a column header to map it to a registration field or skip it.
        </p>
      </div>

      <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <Table
            columns={columns}
            dataSource={dataSource}
            pagination={false}
            size="small"
            scroll={{ x: headers.length * 150 }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px', color: '#6b7280' }}>
        <span>
          <span style={{ color: '#16a34a', fontWeight: 500 }}>{mappedCount}</span> columns will be imported.{' '}
          <span style={{ color: '#9ca3af', fontWeight: 500 }}>{skippedCount}</span> columns will be skipped.
        </span>
        <span style={{ color: '#9ca3af' }}>
          Showing first {Math.min(5, previewData.length)} of {previewData.length} preview rows
        </span>
      </div>

      <ColumnMappingModal
        open={mappingModalOpen}
        onClose={() => {
          setMappingModalOpen(false)
          setSelectedColumn(null)
        }}
        columnHeader={selectedColumn || ''}
        currentMapping={selectedColumn ? columnMapping[selectedColumn] : null}
        registrationFields={registrationFields}
        usedMappings={Object.values(columnMapping).filter(Boolean) as string[]}
        onMappingChange={handleMappingChange}
      />
    </div>
  )
}
